/**
 * Catégories de POI, correspondances OpenStreetMap et familles de besoin.
 * EF-POI-001, EF-IHM-004, DESIGN.md §2.
 *
 * L'ordre du tableau `CATEGORY_ORDER` définit le **rang** d'une catégorie,
 * utilisé pour désigner la catégorie principale d'un POI (EF-POI-010).
 */

export type CategoryKey =
  | 'water'
  | 'bakery'
  | 'convenience'
  | 'food'
  | 'vending'
  | 'fuel'
  | 'hotel'
  | 'guesthouse'
  | 'campsite'
  | 'shelter'
  | 'checkpoint';

export type FamilyKey = 'water' | 'supply' | 'rest' | 'race';

/** Une condition élémentaire sur les tags OSM : clé = valeur, ou clé ∈ valeurs. */
export interface TagCondition {
  readonly key: string;
  readonly values: readonly string[];
}

/** Une correspondance : toutes ses conditions doivent être satisfaites. */
export interface CategoryMatch {
  readonly conditions: readonly TagCondition[];
}

export interface CategoryDefinition {
  readonly key: CategoryKey;
  readonly label: string;
  readonly family: FamilyKey;
  /** Vide pour `checkpoint` : aucune recherche externe, ajout manuel uniquement. */
  readonly matches: readonly CategoryMatch[];
  /** État d'activation initial dans les paramètres de recherche. */
  readonly enabledByDefault: boolean;
  /** `checkpoint` n'est pas une catégorie de recherche. */
  readonly searchable: boolean;
}

const tag = (key: string, ...values: readonly string[]): TagCondition => ({ key, values });

/** L'ordre de ce tableau est normatif : il définit le rang (EF-POI-001). */
export const CATEGORIES: readonly CategoryDefinition[] = [
  {
    key: 'water',
    label: "Point d'eau",
    family: 'water',
    enabledByDefault: true,
    searchable: true,
    matches: [
      { conditions: [tag('amenity', 'drinking_water')] },
      { conditions: [tag('man_made', 'water_tap'), tag('drinking_water', 'yes')] },
      { conditions: [tag('amenity', 'fountain'), tag('drinking_water', 'yes')] },
      { conditions: [tag('natural', 'spring'), tag('drinking_water', 'yes')] },
    ],
  },
  {
    key: 'bakery',
    label: 'Boulangerie',
    family: 'supply',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('shop', 'bakery')] }],
  },
  {
    key: 'convenience',
    label: 'Supérette',
    family: 'supply',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('shop', 'convenience', 'supermarket', 'general', 'greengrocer')] }],
  },
  {
    key: 'food',
    label: 'Restauration',
    family: 'supply',
    enabledByDefault: false,
    searchable: true,
    matches: [{ conditions: [tag('amenity', 'fast_food', 'cafe', 'restaurant')] }],
  },
  {
    key: 'vending',
    label: 'Distributeur automatique',
    family: 'supply',
    enabledByDefault: false,
    searchable: true,
    matches: [
      { conditions: [tag('amenity', 'vending_machine'), tag('vending', 'drinks', 'food', 'sweets', 'coffee')] },
    ],
  },
  {
    key: 'fuel',
    label: 'Station-service',
    family: 'supply',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('amenity', 'fuel')] }],
  },
  {
    key: 'hotel',
    label: 'Hôtel',
    family: 'rest',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('tourism', 'hotel', 'motel')] }],
  },
  {
    key: 'guesthouse',
    label: 'Auberge ou gîte',
    family: 'rest',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('tourism', 'guest_house', 'hostel', 'chalet')] }],
  },
  {
    key: 'campsite',
    label: 'Camping',
    family: 'rest',
    enabledByDefault: true,
    searchable: true,
    matches: [{ conditions: [tag('tourism', 'camp_site')] }],
  },
  {
    key: 'shelter',
    label: 'Abri',
    family: 'rest',
    enabledByDefault: false,
    searchable: true,
    matches: [{ conditions: [tag('amenity', 'shelter')] }, { conditions: [tag('tourism', 'wilderness_hut', 'alpine_hut')] }],
  },
  {
    key: 'checkpoint',
    label: 'Checkpoint',
    family: 'race',
    enabledByDefault: false,
    searchable: false,
    matches: [],
  },
];

export const CATEGORY_ORDER: readonly CategoryKey[] = CATEGORIES.map((c) => c.key);

const BY_KEY = new Map<CategoryKey, CategoryDefinition>(CATEGORIES.map((c) => [c.key, c]));
const RANK = new Map<CategoryKey, number>(CATEGORIES.map((c, i) => [c.key, i]));

export function categoryDefinition(key: CategoryKey): CategoryDefinition {
  const found = BY_KEY.get(key);
  if (found === undefined) throw new Error(`Catégorie inconnue : ${key}`);
  return found;
}

export function isCategoryKey(value: unknown): value is CategoryKey {
  return typeof value === 'string' && BY_KEY.has(value as CategoryKey);
}

/** Rang au sens de EF-POI-001. Plus petit = plus prioritaire. */
export function categoryRank(key: CategoryKey): number {
  const rank = RANK.get(key);
  if (rank === undefined) throw new Error(`Catégorie inconnue : ${key}`);
  return rank;
}

export function categoryFamily(key: CategoryKey): FamilyKey {
  return categoryDefinition(key).family;
}

export function categoryLabel(key: CategoryKey): string {
  return categoryDefinition(key).label;
}

/** Trie une liste de catégories par rang croissant, sans doublon. */
export function sortCategories(keys: readonly CategoryKey[]): readonly CategoryKey[] {
  const unique = [...new Set(keys)];
  unique.sort((a, b) => categoryRank(a) - categoryRank(b));
  return unique;
}

export const SEARCHABLE_CATEGORIES: readonly CategoryKey[] = CATEGORIES.filter((c) => c.searchable).map((c) => c.key);

export const DEFAULT_ENABLED_CATEGORIES: readonly CategoryKey[] = CATEGORIES.filter(
  (c) => c.enabledByDefault,
).map((c) => c.key);

export interface FamilyDefinition {
  readonly key: FamilyKey;
  readonly label: string;
  readonly light: string;
  readonly dark: string;
}

/** Palette normative de EF-IHM-004. Quatre couleurs, deux modes. */
export const FAMILIES: readonly FamilyDefinition[] = [
  { key: 'water', label: 'Eau', light: '#005fa2', dark: '#1e6aaa' },
  { key: 'supply', label: 'Ravitaillement', light: '#9c5003', dark: '#9f5308' },
  { key: 'rest', label: 'Repos', light: '#ac73e8', dark: '#b15fff' },
  { key: 'race', label: 'Course', light: '#00a66a', dark: '#00ac6e' },
];

const FAMILY_BY_KEY = new Map<FamilyKey, FamilyDefinition>(FAMILIES.map((f) => [f.key, f]));

export function familyDefinition(key: FamilyKey): FamilyDefinition {
  const found = FAMILY_BY_KEY.get(key);
  if (found === undefined) throw new Error(`Famille inconnue : ${key}`);
  return found;
}

/**
 * Détermine toutes les catégories d'un objet OSM à partir de ses tags.
 * Un objet peut satisfaire plusieurs correspondances (EF-POI-001, EF-POI-010).
 * Le résultat est ordonné par rang.
 */
export function categoriesForTags(tags: Readonly<Record<string, string>>): readonly CategoryKey[] {
  const found: CategoryKey[] = [];
  for (const definition of CATEGORIES) {
    if (!definition.searchable) continue;
    for (const match of definition.matches) {
      if (match.conditions.every((c) => matchesCondition(tags, c))) {
        found.push(definition.key);
        break;
      }
    }
  }
  return found;
}

function matchesCondition(tags: Readonly<Record<string, string>>, condition: TagCondition): boolean {
  const value = tags[condition.key];
  return value !== undefined && condition.values.includes(value);
}

/**
 * Tags ayant réellement déclenché les catégories retenues. Enregistrés dans
 * `pois[].osm.matchedTags` de l'annexe A.
 */
export function matchedTags(
  tags: Readonly<Record<string, string>>,
  categories: readonly CategoryKey[],
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of categories) {
    for (const match of categoryDefinition(key).matches) {
      if (!match.conditions.every((c) => matchesCondition(tags, c))) continue;
      for (const condition of match.conditions) {
        const value = tags[condition.key];
        if (value !== undefined) result[condition.key] = value;
      }
    }
  }
  return result;
}
